/** Read/write ~/.standardagents/credentials. */
import fs from "node:fs";
import path from "node:path";
import os from "node:os";
import type { CredentialsFile, InstanceCredential } from "./types.ts";

const DIR = path.join(os.homedir(), ".standardagents");
const FILE = path.join(DIR, "credentials");

export function normalizeEndpoint(endpoint: string): string {
  let e = endpoint.trim();
  if (!/^https?:\/\//i.test(e)) e = "http://" + e;
  return e.replace(/\/+$/, "");
}

export function loadCredentials(): CredentialsFile {
  try {
    const raw = fs.readFileSync(FILE, "utf8");
    const parsed = JSON.parse(raw) as CredentialsFile;
    if (!parsed.instances) parsed.instances = {};
    return parsed;
  } catch {
    return { instances: {} };
  }
}

export function getCredential(endpoint: string): InstanceCredential | null {
  const creds = loadCredentials();
  return creds.instances[normalizeEndpoint(endpoint)] ?? null;
}

export function saveCredential(
  cred: InstanceCredential,
  options: { updateDefault?: boolean } = {}
): void {
  const creds = loadCredentials();
  const endpoint = normalizeEndpoint(cred.endpoint);
  creds.instances[endpoint] = { ...cred, endpoint };
  if (options.updateDefault ?? true) {
    creds.default_endpoint = endpoint;
  }
  fs.mkdirSync(DIR, { recursive: true });
  // Credentials contain a bearer token — keep them private.
  fs.writeFileSync(FILE, JSON.stringify(creds, null, 2), { mode: 0o600 });
  try {
    fs.chmodSync(FILE, 0o600);
  } catch {
    // best effort on platforms without chmod
  }
}

/** Remove the saved token for an endpoint (the `/logout` command). */
export function deleteCredential(endpoint: string): void {
  const creds = loadCredentials();
  delete creds.instances[normalizeEndpoint(endpoint)];
  fs.mkdirSync(DIR, { recursive: true });
  fs.writeFileSync(FILE, JSON.stringify(creds, null, 2), { mode: 0o600 });
  try {
    fs.chmodSync(FILE, 0o600);
  } catch {
    // best effort on platforms without chmod
  }
}

export function defaultEndpoint(): string | null {
  return loadCredentials().default_endpoint ?? null;
}

export function saveDefaultEndpoint(endpoint: string): void {
  const creds = loadCredentials();
  creds.default_endpoint = normalizeEndpoint(endpoint);
  fs.mkdirSync(DIR, { recursive: true });
  fs.writeFileSync(FILE, JSON.stringify(creds, null, 2), { mode: 0o600 });
  try {
    fs.chmodSync(FILE, 0o600);
  } catch {
    // best effort on platforms without chmod
  }
}
